import React from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
// Import about image
import aboutImage from "../assets/images/aboutUs.jpg";
import novaImage from "../assets/images/nova-en-nicoline.jpeg";

// Styled components
const StyledAboutSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.background.default,
}));

const StyledAboutContainer = styled(Container)(({ theme }) => ({
  maxWidth: "lg",
}));

const StyledAboutGrid = styled(Grid)(({ theme }) => ({
  alignItems: "center",
}));

const StyledAboutContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const StyledAboutTitle = styled(Typography)<{ component?: React.ElementType }>(
  ({ theme }) => ({
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
  })
);

const StyledAboutText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  lineHeight: 1.8,
}));

const StyledAboutTextItalic = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  lineHeight: 1.8,
  fontStyle: "italic",
}));

const StyledAboutTextBold = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  lineHeight: 1.8,
  fontWeight: "bold",
}));

const StyledAboutImage = styled("img")(({ theme }) => ({
  width: "100%",
  height: "400px",
  objectFit: "cover",
  borderRadius: theme.spacing(1),
}));

export const AboutSection: React.FC = () => {
  return (
    <StyledAboutSection id="about">
      <StyledAboutContainer>
        <StyledAboutGrid container spacing={6}>
          <Grid item xs={12} md={6}>
            <StyledAboutContent>
              <StyledAboutTitle variant="h4">Over Pippaya</StyledAboutTitle>
              <StyledAboutTextBold variant="body1">
                Een hond die bij Pippaya komt logeren, komt eigenlijk gewoon een beetje bij ons thuis.
              </StyledAboutTextBold>
              <StyledAboutText variant="body1">
                Pippaya is ontstaan na het verlies van onze twee meiden. Ineens was het stil in huis. Geen hondenpoten meer om ons heen, geen haren overal, geen natte neuzen en ook niemand meer die precies weet wanneer er ergens een zakje met iets lekkers opengaat. 😉
              </StyledAboutText>
              <StyledAboutText variant="body1">
                Uit dat gemis ontstond langzaam het idee voor Pippaya. Want een huis zonder honden… nee, dat paste eigenlijk helemaal niet bij ons.
              </StyledAboutText>
              <StyledAboutText variant="body1">
                Maar ik wilde geen groot hondenpension beginnen. Geen rijen kennels en geen grote groepen honden. Ik wilde juist een plek maken waar een hond lekker mee kan draaien in het gewone leven. Een beetje alsof er tijdelijk een extra hond bij ons woont. En zo is Pippaya ontstaan.
              </StyledAboutText>
              <StyledAboutTextBold variant="body1">Hier mag een hond vooral gewoon hond zijn.</StyledAboutTextBold>
              <StyledAboutText variant="body1">
                We gaan wandelen, trekken lekker de polder in en als het kan gaan we zwemmen. De ene keer komen we redelijk toonbaar thuis en de andere keer heb ik een stel verzopen moddermonsters bij me. 😂 Maar ach, daar hebben ze handdoeken en een wasmachine voor uitgevonden.
              </StyledAboutText>
              <StyledAboutText variant="body1">
                Er wordt gespeeld en gesnuffeld, er zijn lekkere snackjes en natuurlijk wordt er uitgebreid geknuffeld. Maar het hoeft hier echt niet de hele dag feest te zijn. Na een flinke wandeling of een zwempartij is het minstens zo lekker om ergens neer te ploffen en een paar uur helemaal niets te doen. En dat mag hier ook.
              </StyledAboutText>
              <StyledAboutText variant="body1">
                Iedere hond is anders en juist dát maakt het zo leuk. De één voelt zich binnen vijf minuten volledig thuis en de ander heeft even tijd nodig om alles rustig te bekijken. Er zijn honden die het liefst tegen je aan liggen en honden die liever hun eigen fijne plekje uitzoeken. De één wordt dolgelukkig van water, de ander kijkt ernaar en denkt: <strong>zoek het lekker uit, ik blijf hier wel staan.</strong> 😂
              </StyledAboutText>
              <StyledAboutText variant="body1">
                Daarom houd ik Pippaya bewust klein. Ik wil de honden die hier komen echt leren kennen. Weten waar ze blij van worden, wanneer ze rust nodig hebben en ook die kleine eigenaardigheden ontdekken die iedere hond nou eenmaal heeft. Want juist dát vind ik het leukste aan dit werk.
              </StyledAboutText>

              <StyledAboutTitle variant="h5">En toen kwam Nova 💜</StyledAboutTitle>
              <StyledAboutImage src={novaImage} alt="Nicoline met Nova" sx={{ mb: 3 }} />
              <StyledAboutText variant="body1">
                Sinds <strong>1 augustus 2026</strong> hebben Mark en ik zelf weer een hond in huis: onze Flatcoated Retriever <strong>Nova</strong>. En daarmee begon er voor ons natuurlijk ook weer een heel nieuw avontuur.
              </StyledAboutText>
              <StyledAboutText variant="body1">
                Nova groeit op tussen de logeerhonden van Pippaya. Voor haar is het heel normaal dat er regelmatig andere honden bij ons thuis zijn. De ene keer is er een heerlijke speelkameraad, de andere keer een wat oudere hond die helemaal geen behoefte heeft aan dat enthousiaste puppygedoe. En ook dát moet je als jonge dame leren. 😉
              </StyledAboutText>
              <StyledAboutText variant="body1">
                Nova gaat mee wandelen, ontdekt het zwemmen en is samen met mij volop aan het leren. We trainen samen en doen jachttraining, waar we allebei nog genoeg te leren hebben. En ondertussen heeft ze natuurlijk vooral haar eigen ideeën over hoe het leven eruit zou moeten zien.
              </StyledAboutText>
              <StyledAboutText variant="body1">
                Ze woont hier nog maar sinds 1 augustus, maar Pippaya zonder Nova kunnen we ons eigenlijk nu al niet meer voorstellen.
              </StyledAboutText>

              <StyledAboutTitle variant="h5">Liefde voor honden is de basis</StyledAboutTitle>
              <StyledAboutText variant="body1">
                Pippaya is begonnen vanuit mijn liefde voor honden, maar ik vind het ook belangrijk om te weten wat ik doe. Daarom heb ik inmiddels mijn certificaat <strong>Vakbekwaam Honden &amp; Kat</strong> behaald.
              </StyledAboutText>
              <StyledAboutText variant="body1">
                Want hoe gezellig het hier ook is, mensen vertrouwen mij wel hun hond toe. En daar ga ik serieus mee om. Ik let erop of een hond lekker in z'n vel zit, zorg voor voldoende rust en kijk goed welke honden bij elkaar passen. Niet iedere hond hoeft met iedere andere hond dikke vrienden te worden. Als iedereen zich hier maar veilig en prettig voelt.
              </StyledAboutText>
              <StyledAboutText variant="body1">En juist omdat Pippaya zo kleinschalig is, kan dat ook.</StyledAboutText>

              <StyledAboutTitle variant="h5">Gewoon Pippaya</StyledAboutTitle>
              <StyledAboutText variant="body1">
                Soms is het hier een gezellige bende. Rennen door de tuin, speelgoed overal, natte poten over de vloer en honden die allemaal tegelijk lijken te bedenken dat ze iets van me willen. En soms? Dan ligt de hele club voor pampus en hoor je helemaal niets. Ik vind het allebei heerlijk.
              </StyledAboutText>
              <StyledAboutTextBold variant="body1">Geen kennel. Geen massale opvang. Geen hondenhotel.</StyledAboutTextBold>
              <StyledAboutText variant="body1">
                Wel wandelen, zwemmen, spelen, snuffelen, modderpoten, lekkere snackjes, knuffels, fijne slaapplekjes en vooral veel aandacht voor iedere hond die hier komt logeren.
              </StyledAboutText>
              <StyledAboutText variant="body1">
                En terwijl de baasjes ondertussen ergens lekker van hún vakantie genieten, wordt er bij Pippaya gewoon minstens zo hard vakantie gevierd. 😉
              </StyledAboutText>
              <StyledAboutTextBold variant="body1">Wie heeft er nou eigenlijk de beste vakantie? De baasjes of de hond? 😂🐾</StyledAboutTextBold>
              <StyledAboutText variant="body1">En precies zo hoort het wat mij betreft te zijn.</StyledAboutText>
              <StyledAboutTextBold variant="body1">
                Welkom bij Pippaya. Waar de baasjes op vakantie gaan… en de honden stiekem misschien nog nét iets meer. ❤️🐾
              </StyledAboutTextBold>
            </StyledAboutContent>
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledAboutImage src={aboutImage} alt="Huisdieropvang sfeer" />
          </Grid>
        </StyledAboutGrid>
      </StyledAboutContainer>
    </StyledAboutSection>
  );
};
